// src/components/feature/FavoriteButton.tsx
//
// The favourites toggle. Rendered on the product card and on the
// product detail page.
//
// It renders NOTHING for an anonymous visitor: a write toggle that
// cannot work is worse than no toggle at all. The signed-in check
// lives in FavoritesProvider (canWrite).
//
// Accessibility: the button carries an accessible NAME that states
// the ACTION ("Add to favorites" / "Remove from favorites") and
// aria-pressed states whether the product is currently a favourite.
// The error message is announced politely (role="status") so a failed
// write is not silent.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heart, Loader2 } from "lucide-react";
import { useFavorites } from "@/components/feature/FavoritesProvider";
import { cn } from "@/lib/cn";

export interface FavoriteButtonProps {
  productId: number;
  /** Product name, used in the accessible name. */
  productName?: string;
  /** Render the label next to the icon (detail page) instead of an
   *  icon-only button (card). */
  withLabel?: boolean;
  className?: string;
}

export function FavoriteButton({
  productId,
  productName,
  withLabel = false,
  className,
}: FavoriteButtonProps) {
  const { t } = useTranslation();
  const { canWrite, isFavorite, isPending, isLocked, toggle } = useFavorites();
  const [error, setError] = useState(false);

  // Anonymous visitor (or no session): NO write toggle at all.
  if (!canWrite) return null;

  const favorite = isFavorite(productId);
  const pending = isPending(productId);
  // Inside the post-write cooldown a click would be absorbed to
  // protect against a double-click. The button therefore says so: it
  // is disabled, with the wait cursor. It must not LOOK clickable
  // while quietly ignoring the click.
  const locked = isLocked(productId);
  const action = favorite
    ? t("favorites.remove", "Remove from favorites")
    : t("favorites.add", "Add to favorites");
  const name = productName
    ? `${action}: ${productName}`
    : action;

  async function onClick() {
    if (pending || locked) return;
    setError(false);
    const result = await toggle(productId);
    // null === the write failed. The list is NOT changed optimistically,
    // so nothing to revert — but the failure must be visible.
    if (result === null) setError(true);
  }

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={onClick}
        disabled={pending || locked}
        aria-pressed={favorite}
        aria-label={name}
        title={name}
        data-testid={`favorite-toggle-${productId}`}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-indigo focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas",
          "disabled:opacity-60 disabled:cursor-wait",
          favorite
            ? "border-accent-rose/50 bg-accent-rose/15 text-accent-rose"
            : "border-border bg-bg-surface/90 text-fg-secondary hover:border-accent-rose/40 hover:text-accent-rose",
          withLabel ? "px-3 py-1.5 text-xs font-510" : "h-8 w-8 justify-center",
          className,
        )}
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        ) : (
          <Heart
            className="h-4 w-4"
            strokeWidth={1.5}
            fill={favorite ? "currentColor" : "none"}
            aria-hidden
          />
        )}
        {withLabel && (
          <span>{favorite
            ? t("favorites.remove", "Remove from favorites")
            : t("favorites.add", "Add to favorites")}</span>
        )}
      </button>
      {error && (
        <span role="status" className="text-[10px] text-accent-rose">
          {t("favorites.error", "Could not update favorites. Please try again.")}
        </span>
      )}
    </span>
  );
}
